import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, XCircle, AlertTriangle, Globe, Shield, Link2,
  FileText, BarChart3, Bell, Package, RefreshCw, Rocket, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';



const CHECK_CATEGORIES = [
  {
    id: 'domain',
    label: 'Domain & SSL',
    icon: Globe,
    checks: [
      { id: 'domain_set', label: 'Domain tanimli', critical: true },
      { id: 'domain_reachable', label: 'Domain erisilebilir (HTTPS)', critical: true },
      { id: 'ssl_valid', label: 'SSL sertifikasi gecerli', critical: true },
      { id: 'ssl_not_expiring', label: 'SSL 30+ gun gecerli', critical: false },
    ]
  },
  {
    id: 'content',
    label: 'Icerik & Sayfalar',
    icon: FileText,
    checks: [
      { id: 'has_homepage', label: 'Ana sayfa (/) mevcut', critical: true },
      { id: 'has_5_pages', label: 'En az 5 sayfa uretilmis', critical: false },
      { id: 'all_seo_titles', label: 'Tum sayfalarda SEO basligi var', critical: true },
      { id: 'all_meta_desc', label: 'Tum sayfalarda meta aciklama var', critical: true },
      { id: 'published_pages', label: 'Yayinda sayfalar mevcut', critical: false },
    ]
  },
  {
    id: 'affiliate',
    label: 'Affiliate & Redirect',
    icon: Link2,
    checks: [
      { id: 'affiliate_set', label: 'Affiliate URL tanimli', critical: true },
      { id: 'affiliate_reachable', label: 'Affiliate URL erisilebilir', critical: true },
      { id: 'fallback_set', label: 'Yedek affiliate URL tanimli', critical: false },
      { id: 'redirect_rules', label: 'Redirect kurallari ayarli', critical: false },
    ]
  },
  {
    id: 'seo',
    label: 'SEO Hazirlik',
    icon: BarChart3,
    checks: [
      { id: 'sitemap_ready', label: 'Sitemap.xml uretilecek', critical: true },
      { id: 'robots_ready', label: 'Robots.txt uretilecek', critical: true },
      { id: 'internal_links', label: 'Ic linkler olusturulmus', critical: false },
      { id: 'no_critical_seo', label: 'Kritik SEO sorunu yok', critical: false },
    ]
  },
  {
    id: 'tracking',
    label: 'Tracking & Analytics',
    icon: BarChart3,
    checks: [
      { id: 'events_tracking', label: 'Event tracking aktif', critical: false },
      { id: 'revenue_configured', label: 'Gelir takibi ayarli', critical: false },
    ]
  },
  {
    id: 'monitoring',
    label: 'Izleme & Guvenlik',
    icon: Bell,
    checks: [
      { id: 'no_open_critical', label: 'Acik kritik alert yok', critical: true },
      { id: 'deploy_ready', label: 'Deploy paketi hazir', critical: true },
    ]
  },
];

export default function LaunchChecklistPage() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get('/sites').then(res => {
      setSites(res.data);
      if (res.data.length > 0) setSelectedSite(res.data[0].id);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const runChecks = useCallback(async () => {
    if (!selectedSite) return;
    setChecking(true);
    const r = {};

    try {
      // Fetch all data needed
      const [siteRes, pagesRes, auditRes, alertsRes, reportRes, healthRes, revenueRes] = await Promise.all([
        api.get(`/sites/${selectedSite}`),
        api.get(`/sites/${selectedSite}/pages`),
        api.get(`/sites/${selectedSite}/seo/audit`),
        api.get('/monitoring/alerts', { params: { site_id: selectedSite, status: 'open' } }),
        api.get(`/deploy/staging-report?site_id=${selectedSite}`),
        api.get(`/sites/${selectedSite}/domain-health`).catch(() => ({ data: [] })),
        api.get('/revenue/entries', { params: { site_id: selectedSite } }),
      ]);

      const site = siteRes.data;
      const pages = pagesRes.data;
      const audit = auditRes.data;
      const alerts = alertsRes.data;
      const report = reportRes.data;
      const healthChecks = healthRes.data;
      const lastHealth = healthChecks[0] || {};
      const revenue = revenueRes.data;

      // Domain checks
      r.domain_set = !!site.domain;
      r.domain_reachable = lastHealth.status === 'healthy';
      r.ssl_valid = lastHealth.ssl_valid === true;
      r.ssl_not_expiring = lastHealth.ssl_valid === true; // Simplified

      // Content checks
      r.has_homepage = pages.some(p => p.slug === '/');
      r.has_5_pages = pages.length >= 5;
      r.all_seo_titles = pages.length > 0 && pages.every(p => p.seo_title);
      r.all_meta_desc = pages.length > 0 && pages.every(p => p.meta_description);
      r.published_pages = pages.some(p => p.status === 'published' || p.workflow_status === 'published');

      // Affiliate checks
      r.affiliate_set = !!site.affiliate_url;
      r.affiliate_reachable = true; // Would need async check
      r.fallback_set = !!site.fallback_affiliate_url;
      r.redirect_rules = !!site.redirect_rules?.type;

      // SEO checks
      r.sitemap_ready = pages.length > 0;
      r.robots_ready = true;
      r.internal_links = pages.some(p => p.internal_links?.length > 0);
      r.no_critical_seo = !(audit.items || []).some(i => i.severity === 'critical');

      // Tracking
      r.events_tracking = true; // Event endpoint is always available
      r.revenue_configured = revenue.length > 0;

      // Monitoring
      r.no_open_critical = !alerts.some(a => a.severity === 'critical');
      r.deploy_ready = report.deploy_ready;

      // Run affiliate check
      try {
        const affCheck = await api.post('/monitoring/affiliate-check', { url: site.affiliate_url });
        r.affiliate_reachable = affCheck.data.reachable;
      } catch { r.affiliate_reachable = false; }

      // Run domain health if no recent check
      if (!lastHealth.status) {
        try {
          const healthCheck = await api.post(`/sites/${selectedSite}/domain-health/check`);
          r.domain_reachable = healthCheck.data.status === 'healthy';
          r.ssl_valid = healthCheck.data.ssl_valid === true;
        } catch {}
      }

    } catch (err) {
      console.error('Check failed:', err);
      toast.error('Kontrol basarisiz');
    }

    setResults(r);
    setChecking(false);
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite) runChecks();
  }, [selectedSite, runChecks]);

  // Calculate score
  const allChecks = CHECK_CATEGORIES.flatMap(c => c.checks);
  const passedCount = allChecks.filter(c => results[c.id] === true).length;
  const totalCount = allChecks.length;
  const criticalFails = allChecks.filter(c => c.critical && results[c.id] === false).length;
  const score = totalCount > 0 ? Math.round((passedCount / totalCount) * 100) : 0;
  const isReady = criticalFails === 0 && score >= 70;

  const selectedSiteData = sites.find(s => s.id === selectedSite);

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 rounded-xl shimmer" />)}</div>;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Canliya Alma Kontrol Listesi</h1>
          <p className="text-sm text-muted-foreground mt-1">Production launch oncesi dogrulama</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[260px] bg-secondary/40 border-border/70"><SelectValue placeholder="Site secin" /></SelectTrigger>
            <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name} ({s.domain})</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={runChecks} disabled={checking} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Kontrol Et
          </Button>
        </div>
      </motion.div>

      {/* Score Header */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className={`border-border/70 ${isReady ? 'gold-glow' : ''}`}>
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${isReady ? 'bg-emerald-400/10' : score >= 50 ? 'bg-amber-400/10' : 'bg-red-400/10'}`}>
                  {isReady ? <Rocket className="w-10 h-10 text-emerald-400" /> : 
                   score >= 50 ? <AlertTriangle className="w-10 h-10 text-amber-400" /> :
                   <XCircle className="w-10 h-10 text-red-400" />}
                </div>
                <div>
                  <h2 className="text-xl font-semibold font-['Space_Grotesk']">
                    {selectedSiteData?.brand_name || ''}
                    <span className="text-sm font-normal text-muted-foreground ml-2">{selectedSiteData?.domain || ''}</span>
                  </h2>
                  <p className={`text-lg font-semibold mt-1 ${isReady ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    {isReady ? 'Canliya Almaya Hazir!' : criticalFails > 0 ? `${criticalFails} Kritik Sorun Var` : 'Iyilestirme Gerekiyor'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-4xl font-bold font-['Space_Grotesk'] ${isReady ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  %{score}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{passedCount}/{totalCount} kontrol gecti</p>
              </div>
            </div>
            <Progress value={score} className="mt-4 h-2" />
          </CardContent>
        </Card>
      </motion.div>

      {/* Check Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {CHECK_CATEGORIES.map((cat, catIdx) => {
          const Icon = cat.icon;
          const catPassed = cat.checks.filter(c => results[c.id] === true).length;
          const catTotal = cat.checks.length;
          return (
            <motion.div key={cat.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + catIdx * 0.05 }}>
              <Card className="border-border/70">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-muted-foreground" /> {cat.label}</span>
                    <span className="text-xs text-muted-foreground">{catPassed}/{catTotal}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {cat.checks.map(check => {
                      const passed = results[check.id];
                      const status = passed === true ? 'pass' : passed === false ? 'fail' : 'unknown';
                      return (
                        <div key={check.id} className="flex items-center justify-between py-1.5">
                          <div className="flex items-center gap-2.5">
                            {status === 'pass' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> :
                             status === 'fail' ? <XCircle className={`w-4 h-4 ${check.critical ? 'text-red-400' : 'text-amber-400'}`} /> :
                             <div className="w-4 h-4 rounded-full border border-border/70" />}
                            <span className={`text-sm ${status === 'fail' && check.critical ? 'text-red-400' : ''}`}>{check.label}</span>
                          </div>
                          {check.critical && status === 'fail' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-400/10 text-red-400">Kritik</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
